<script setup lang="ts">
import { computed } from 'vue';
import type { EditorInstance } from '@/helpers/LabelingSerialization';
import gettext from '@/i18n/gettext';

const { $gettext } = gettext;

const props = defineProps<{
  instance: EditorInstance | null;
  // List position of the selected instance (ids keep counting after deletes).
  instanceNumber: number | null;
  categories: string[];
  rotationEnabled: boolean;
  doneHint: string | null;
  readOnly: boolean;
  cutoutUrl: string | null;
}>();

const emit = defineEmits<{
  'set-class': [name: string];
  'set-angle': [deg: number];
  'toggle-rotation': [];
}>();

const keypointText = computed(() => {
  if (!props.instance?.keypoint) {
    return $gettext('Not set');
  }
  const [x, y] = props.instance.keypoint;
  return `${Math.round(x)}, ${Math.round(y)} px`;
});

// The UI shows the angle as a full 0–360° turn (roast feedback: a −180°
// default "pointing the same way" as 180° confused everyone). Stored angles
// and the saved annotations keep the pipeline's −180..180 convention.
const displayAngle = computed(
  () => (((props.instance?.angleDeg ?? 0) % 360) + 360) % 360,
);

function setDisplayAngle(deg: number) {
  emit('set-angle', ((deg + 180) % 360) - 180);
}
</script>

<template>
  <div class="details-panel d-flex flex-column">
    <div class="d-flex align-center justify-space-between px-3 pt-3 pb-2">
      <h4 class="text-subtitle-2">{{ $gettext('Details') }}</h4>
      <!-- flat: the tonal variant is barely readable on the panel background -->
      <v-chip
        v-if="instance"
        size="small"
        color="primary"
        variant="flat"
        density="comfortable"
      >
        {{ $gettext('Selected') }} · #{{ instanceNumber }}
      </v-chip>
    </div>
    <div class="flex-grow-1 overflow-y-auto px-3 pb-3">
      <div class="text-caption text-medium-emphasis mb-1">
        {{ $gettext('Class') }}
      </div>
      <v-select
        :model-value="instance?.categoryName || null"
        :items="categories"
        :disabled="!instance || readOnly"
        density="compact"
        variant="outlined"
        hide-details
        @update:model-value="
          (name: string | null) => name && emit('set-class', name)
        "
      />

      <div class="d-flex align-center justify-space-between mt-4 mb-1">
        <span class="text-caption text-medium-emphasis">
          {{ $gettext('Rotation') }}
        </span>
        <v-switch
          :model-value="rotationEnabled"
          color="primary"
          density="compact"
          inset
          hide-details
          :disabled="readOnly"
          :aria-label="$gettext('Rotation labeling')"
          class="rotation-switch flex-grow-0"
          @update:model-value="emit('toggle-rotation')"
        />
      </div>
      <template v-if="rotationEnabled">
        <v-card rounded="lg" variant="flat" class="pa-3 d-flex align-center">
          <v-img
            v-if="cutoutUrl"
            :src="cutoutUrl"
            width="64"
            height="64"
            class="cutout-thumb flex-grow-0 mr-3"
          />
          <div>
            <div class="text-h4">{{ Math.round(displayAngle) }}°</div>
            <div class="text-caption text-medium-emphasis">
              {{ $gettext('Measured on the part cutout — zero points up') }}
            </div>
          </div>
        </v-card>
        <v-slider
          :model-value="displayAngle"
          :min="0"
          :max="360"
          :step="1"
          :disabled="!instance || readOnly"
          density="compact"
          hide-details
          class="mt-1"
          @update:model-value="setDisplayAngle"
        />
      </template>
      <v-card v-else rounded="lg" variant="flat" class="pa-3">
        <span class="text-caption text-medium-emphasis">
          {{
            $gettext(
              'Rotation labeling is deactivated for this project — no angle is stored with the mask.',
            )
          }}
        </span>
      </v-card>

      <div class="text-caption text-medium-emphasis mt-4 mb-1">
        {{ $gettext('Pick-point') }}
      </div>
      <div class="d-flex align-center text-body-2">
        <v-icon icon="mdi-target" size="small" color="pink" class="mr-2" />
        {{ keypointText }}
      </div>
      <div class="text-caption text-medium-emphasis mt-1">
        {{
          $gettext(
            'Placed at the mask center when the mask is created — use the pick-point tool (click or arrow keys) to adjust it.',
          )
        }}
      </div>

      <v-alert
        v-if="doneHint"
        type="warning"
        density="compact"
        variant="tonal"
        class="mt-4 text-caption"
      >
        {{ doneHint }}
      </v-alert>
    </div>
  </div>
</template>

<style scoped>
.details-panel {
  width: 260px;
  flex: 0 0 auto;
  border-right: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgba(var(--v-theme-on-surface), 0.03);
  min-height: 0;
}
/* Small displays: the fixed side panels must not squeeze the canvas away. */
@media (max-width: 1280px) {
  .details-panel {
    width: 210px;
  }
}
/* The default switch reserves input-field height — trim it to the row. */
.rotation-switch :deep(.v-selection-control) {
  min-height: 0;
}
.cutout-thumb {
  background: #0d2130;
  border-radius: 8px;
}
</style>
